import React, { useState, useEffect, useCallback } from 'react';
import { useAuth, AuthProvider } from './contexts/AuthContext';
import AuthScreen from './components/AuthScreen';
import SignUpScreen from './components/SignUpScreen';
import EquipmentContent from './components/EquipmentContent';
import LandingPage from './components/LandingPage';
import UserTopPage from './components/UserTopPage';
import HousePlanner from './features/house_planner/HouserPlanner';
import VisitReservation from './components/VisitReservation';
import ConfirmedSummary from './components/ConfirmedSummary';
import './index.css'; // 必要に応じてスタイルシートをインポート
// react-router-dom のコンポーネントをインポート
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, useParams } from 'react-router-dom';
import { Box } from '@chakra-ui/react';
import AdminUserDetail from './components/AdminUserDetail';
import UserManagement from './components/UserManagement';
import LikeImagesWithChat from './components/LikeImagesWithChat';
import FloorPlanner from './features/house_planner/components/FloorPlanner';
import UserInviteAccept from './components/UserInviteAccept';
import AnythingChat from './components/AnythingChat';
import UserInfo from './components/UserInfo';
import MasterEquipment from './components/MasterEquipment';
import MasterEquipmentDefinitions from './components/MasterEquipmentDefinitions';
import EmailNotVerified from './components/EmailNotVerified';
import ExcludedItemsManagement from './components/ExcludedItemsManagement';
import BreadcrumbComponent from './components/Breadcrumb';
import CommonHeader from './components/CommonHeader';
import UserGuide from './components/UserGuide';
import { getUnreadItemTypes, getUnreadItemTypesByUserId } from './services/api';

// 認証が必要なルートを保護するコンポーネント
const ProtectedRoute = ({ children }) => {
  const { currentUser, isAdmin, loading, isEmailVerified } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!currentUser) {
    return <Navigate to="/" replace />;
  }

  if (!isEmailVerified) {
    return <EmailNotVerified />;
  }

  return children;
};

// 管理者用の保護されたルート
const AdminRoute = ({ children }) => {
  const { currentUser, isAdmin, loading, isEmailVerified } = useAuth();

  if (loading) {
    return <div>Loading...</div>;
  }

  // 管理者でなければランディングページへリダイレクト
  if (!currentUser || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (!isEmailVerified) {
    return <EmailNotVerified />;
  }

  return children;
};

function AppContent() {
  const { loading, isFunctionEnabled, currentUser, isAdmin } = useAuth(); // loading 状態はここでチェック
  const location = useLocation();
  const [unreadItemTypes, setUnreadItemTypes] = useState([]);

  // 未読状態を取得する関数
  const fetchUnreadItemTypes = useCallback(async () => {
    if (!currentUser || !isFunctionEnabled('chat')) {
      setUnreadItemTypes([]);
      return;
    }

    try {
      const unreadItemTypes = await getUnreadItemTypes();
      setUnreadItemTypes(unreadItemTypes.item_types);
    } catch (error) {
      console.error('未読アイテムタイプの取得に失敗:', error);
      setUnreadItemTypes([]);
    }
  }, [currentUser, isFunctionEnabled]);

  // 初期ロード時に未読通知を取得し、その後30秒間隔で更新
  useEffect(() => {
    if (currentUser) {
      fetchUnreadItemTypes();
      
      if (isFunctionEnabled('chat')) {
        const intervalId = setInterval(() => {
          fetchUnreadItemTypes();
        }, 30000); // 30秒ごとに更新
        
        return () => clearInterval(intervalId);
      }
    }
  }, [fetchUnreadItemTypes, currentUser, isFunctionEnabled]);

  // ログイン前の画面かどうかを判定
  const isPublicPage = !currentUser || 
    location.pathname === '/' || 
    location.pathname === '/auth' || 
    location.pathname === '/signup' || 
    location.pathname.startsWith('/user_invite/');

  if (loading) {
    // アプリケーション全体のローディング表示
    return <div>アプリケーションを読み込んでいます...</div>;
  }

  return (
    <>
      {/* ログイン後の画面でのみCommonHeaderを表示 */}
      {currentUser && (
        <CommonHeader unreadItemTypes={unreadItemTypes} />
      )}
      <BreadcrumbComponent />
      <Routes>
        {/* 認証画面用のルート（レガシー対応） */}
        <Route path="/auth" element={
          currentUser ? (isAdmin ? <Navigate to="/admin/users" replace /> : <Navigate to="/" replace />) : <AuthScreen />
        } />
        
        {/* 新規登録画面用のルート（レガシー対応） */}
        <Route path="/signup" element={
          currentUser ? (isAdmin ? <Navigate to="/admin/users" replace /> : <Navigate to="/" replace />) : <SignUpScreen />
        } />

        {/* 招待画面用のルート */}
        <Route path="/user_invite/accept" element={<UserInviteAccept />} />

        {/* メインのランディングページ（認証不要） */}
        <Route
          path="/"
          element={
            currentUser ? (isAdmin ? <Navigate to="/admin/users" replace /> : <UserTopPage />) : <LandingPage />
          }
        />

        {/* 設備選択画面用のルート (認証が必要) */}
        <Route
          path="/equipment"
          element={
            <ProtectedRoute>
              {/* EquipmentContent には onFixedChange が必要なら渡す */}
              <EquipmentContent onFixedChange={() => {}} />
            </ProtectedRoute>
          }
        />

        {/* 管理者用の顧客の設備選択画面用のルート */}
        <Route
          path="/admin/user/:targetUserId/equipment"
          element={
            <AdminRoute>
              <EquipmentContent onFixedChange={() => {}} />
            </AdminRoute>
          }
        />

        {/* 間取りプランナー用のルート (認証が必要) */}
        <Route
          path="/planner" // '/planner' パスにアクセスした場合
          element={
            <ProtectedRoute>
              <HousePlanner /> {/* HousePlanner コンポーネントを表示 */}
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin/user/:targetUserId/planner" // '/planner' パスにアクセスした場合
          element={
            <AdminRoute>
              <HousePlanner /> {/* HousePlanner コンポーネントを表示 */}
            </AdminRoute>
          }
        />

        <Route
          path="/admin/master-equipment" 
          element={
            <AdminRoute>
              <MasterEquipment /> {/* MasterEquipment コンポーネントを表示 */}
            </AdminRoute>
          }
        />

        <Route
          path="/admin/master-equipment/master-equipment-definitions" 
          element={
            <AdminRoute>
              <MasterEquipmentDefinitions /> {/* MasterEquipmentDefinitions コンポーネントを表示 */}
            </AdminRoute>
          }
        />

        <Route
          path="/admin/master-equipment/master-floor" 
          element={
            <AdminRoute>
              <HousePlanner /> {/* HouserPlanner コンポーネントを表示 */}
            </AdminRoute>
          }
        />


        {/* 来店予約画面用のルート (認証が必要) */}
        <Route
          path="/visit-reservation"
          element={
            <ProtectedRoute>
              <VisitReservation />
            </ProtectedRoute>
          }
        />

        {/* 確定アイテム一覧用のルート (認証が必要) */}
        <Route
          path="/equipment/confirmed-summary"
          element={
            <ProtectedRoute>
              <ConfirmedSummary />
            </ProtectedRoute>
          }
        />

        {/* 管理者用の顧客の確定アイテム一覧用のルート */}
        <Route
          path="/admin/user/:targetUserId/equipment/confirmed-summary"
          element={
            <AdminRoute>
              <ConfirmedSummary />
            </AdminRoute>
          }
        />

        <Route
          path="/like-images"
          element={
            <ProtectedRoute>
              <LikeImagesWithChat />
            </ProtectedRoute>
          }
        />

        {/* 管理者用の顧客の確定アイテム一覧用のルート */}
        <Route
          path="/admin/user/:targetUserId/like-images"
          element={
            <AdminRoute>
              <LikeImagesWithChat />
            </AdminRoute>
          }
        />

        {/* チャット機能が有効な場合のみルートを表示 */}
        {isFunctionEnabled('chat') && (
          <>
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <AnythingChat />
                </ProtectedRoute>
              }
            />

            {/* 管理者用の顧客の確定アイテム一覧用のルート */}
            <Route
              path="/admin/user/:targetUserId/chat"
              element={
                <AdminRoute>
                  <AnythingChat />
                </AdminRoute>
              }
            />
          </>
        )}  

        <Route
          path="/admin/user/:targetUserId/planner"
          element={
            <AdminRoute>
              <HousePlanner />
            </AdminRoute>
          }
        />

        {/* 管理者用ルート */}
        <Route
          path="/admin/users"
          element={
            <AdminRoute>
              <UserManagement />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/user/:userId"
          element={
            <AdminRoute>
              <AdminUserDetail />
            </AdminRoute>
          }
        />
        <Route
          path="/admin/user-info"
          element={ 
            <AdminRoute>
              <UserInfo isAdmin={true} />
            </AdminRoute>
          }
        />
        <Route
          path="/user-info"
          element={
            <ProtectedRoute>
              <UserInfo />
            </ProtectedRoute>
          }
        />

        {/* 見積もり外項目管理（管理者用・マスタ管理の一部） */}
        <Route
          path="/admin/master-equipment/excluded-items"
          element={
            <AdminRoute>
              <ExcludedItemsManagement />
            </AdminRoute>
          }
        />

        {/* 使い方ガイド（認証が必要） */}
        <Route
          path="/guide"
          element={
            <ProtectedRoute>
              <UserGuide />
            </ProtectedRoute>
          }
        />

        {/* 管理者用の顧客の使い方ガイド */}
        <Route
          path="/admin/user/:targetUserId/guide"
          element={
            <AdminRoute>
              <UserGuide />
            </AdminRoute>
          }
        />

        {/* 上記以外のパスの処理 */}
        <Route path="*" element={
          currentUser ? (isAdmin ? <Navigate to="/admin/users" replace /> : <Navigate to="/" replace />) : <Navigate to="/" replace />
        } />
      </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <Router>
        <AppContent />
      </Router>
    </AuthProvider>
  );
}
